import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminApiError } from '@/services/adminApiFetch';

const ADMIN_SECRET = import.meta.env.VITE_ADMIN_PATH_SECRET ?? '__ops_dev';

const loginSchema = z.object({
  email: z.string().email({ message: 'auth.email_invalid' }),
  password: z.string().min(1, { message: 'common.required' }),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function AdminLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAdminAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const fromPath =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    `/${ADMIN_SECRET}`;

  async function onSubmit(values: LoginFormValues) {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 401) {
        setSubmitError(t('admin.login.error'));
      } else {
        setSubmitError(t('common.error_generic'));
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="h-14 flex items-center px-6 border-b border-border">
        <span className="text-sm font-semibold">Babilu · ops</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-1.5 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">{t('admin.login.title')}</h1>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.login.email')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        autoFocus
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin.login.password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError && (
                <div role="alert" className="text-sm text-destructive">
                  {submitError}
                </div>
              )}

              <Button type="submit" className="w-full h-11" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? t('common.loading') : t('admin.login.submit')}
              </Button>
            </form>
          </Form>
        </div>
      </main>
    </div>
  );
}
